/*** IMPORTS FROM imports-loader ***/
var define = false;

import leftIcon from '@SvgIcon/chevron-left.svg';
import rightIcon from '@SvgIcon/chevron-right.svg';
import listIcon from '@SvgIcon/android-list.svg';
import calendarIcon from '@SvgIcon/calendar.svg';

import each from 'lodash/each';
import get from 'lodash/get';
import set from 'lodash/set';
import $ from 'jquery';
import moment from 'moment';

import svgIconHtml from '../../../shared/svgIconHtml';
import ListEvent from './ListEvent';
import EventInstanceModal from './EventInstanceModal';

export default {
  name: 'Calendar',

  components: {
    ListEvent,
    EventInstanceModal,
  },

  props: {
    eventsUrl: {
      type: String,
      required: true,
    },

    createUrl: {
      type: String,
      required: true,
    },

    initialDate: {
      type: String,
      required: true,
    },

    initialLocation: {
      type: String,
      required: true,
    },

    locations: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      mode: 'month',
      date: moment(this.initialDate),
      location: '*',
      rawEvents: {},
      loadingEvents: false,
      loadedEventsOnce: false,
    };
  },

  computed: {
    today() {
      return moment().set({
        hour: 0, minute: 0, second: 0, millisecond: 0,
      });
    },

    nextYear() {
      return this.today.clone().add(1, 'year');
    },

    startingDayOfWeek() {
      return 0; // Sunday
    },

    periodStart() {
      return moment({ year: this.date.year(), month: this.date.month(), day: 1 });
    },

    periodEnd() {
      return moment({ year: this.date.year(), month: this.date.month(), day: this.date.daysInMonth() });
    },

    displayFirstDate() {
      return this.beginningOfWeek(this.periodStart, this.startingDayOfWeek);
    },

    displayLastDate() {
      return this.endOfWeek(this.periodEnd, this.startingDayOfWeek);
    },

    weeksOfPeriod() {
      const periodDayLength = Math.abs(this.displayFirstDate.diff(this.displayLastDate, 'days'));
      const numWeeks = Math.floor((periodDayLength + 1) / 7);

      return Array(numWeeks).fill(null).map((_, i) => this.displayFirstDate.clone().add(i * 7, 'days'));
    },

    allowGoPrevMonth() {
      return this.date.clone().subtract(1, 'months').isSameOrAfter(this.today, 'month');
    },

    allowGoNextMonth() {
      return this.date.clone().add(1, 'months').isSameOrBefore(this.nextYear, 'month');
    },

    events() {
      if (this.isListMode) {
        return this.rawEvents;
      }

      if (this.isMonthMode) {
        const eventsObject = {};

        each(this.rawEvents, (event) => {
          const eventEnd = moment(event.endDate);
          const tempDate = moment(event.startDate);

          while (tempDate.isSameOrBefore(eventEnd)) {
            const date = tempDate.format('YYYY-MM-DD');
            if (!(date in eventsObject)) {
              eventsObject[date] = [];
            }

            eventsObject[date].push(event);
            tempDate.add({ days: 1 });
          }
        });

        return eventsObject;
      }

      return [];
    },

    filteredEvents() {
      if (this.isListMode) {
        return this.events.filter(event => moment(event.endDate).isSameOrAfter(this.today));
      }

      if (this.isMonthMode) {
        const filtered = {};

        Object.keys(this.events).sort().forEach((key) => {
          if (moment(key).isSameOrAfter(this.today)) {
            filtered[key] = this.events[key];
          }
        });

        return filtered;
      }

      return [];
    },

    isListMode() {
      return this.mode === 'list';
    },

    isMonthMode() {
      return this.mode === 'month';
    },
  },

  watch: {
    mode() {
      this.refreshEvents();
    },

    date() {
      this.refreshEvents();
    },

    location() {
      this.refreshEvents();
    },
  },

  methods: {
    goPrevMonth() {
      this.date = this.date.clone().subtract(1, 'months');
    },

    goNextMonth() {
      this.date = this.date.clone().add(1, 'months');
    },

    beginningOfWeek(date, startDow) {
      return date.clone().add((startDow - date.day() - 7) % -7, 'days');
    },

    endOfWeek(date, startDow) {
      return this.beginningOfWeek(date, startDow).add(7, 'days');
    },

    daysOfWeek(weekStart) {
      return Array(7).fill(null).map((_, i) => weekStart.clone().add(i, 'days'));
    },

    isSameMonth(date1, date2) {
      return date1.month() === date2.month();
    },

    eventsOfDate(date) {
      const ymd = date.format('YYYY-MM-DD');

      if (!(ymd in this.filteredEvents)) {
        return [];
      }

      return this.filteredEvents[ymd];
    },

    getCachedEvents(location, range) {
      return get(this.cache, [location, range], null);
    },

    cacheEvents(location, range, events) {
      return set(this.cache, [location, range], events);
    },

    refreshEvents() {
      if (this.eventsXhr) {
        this.eventsXhr.abort();
      }

      this.loadingEvents = false;
      clearTimeout(this.eventsLoadingTimer);

      const rangeString = this.isListMode
        ? this.periodStart.format('YYYY-MM-DD')
        : `${this.periodStart.format('YYYY-MM-DD')}-${this.periodEnd.format('YYYY-MM-DD')}`;

      const cachedEvents = this.getCachedEvents(this.location, rangeString);
      if (cachedEvents) {
        this.rawEvents = cachedEvents;
        return;
      }

      this.rawEvents = [];

      this.eventsLoadingTimer = setTimeout(() => {
        this.loadingEvents = true;
      }, 300);

      this.eventsXhr = $.ajax({
        type: 'GET',
        url: this.eventsUrl,
        data: {
          mode: this.mode,
          start: this.periodStart.format('YYYY-MM-DD'),
          end: this.periodEnd.format('YYYY-MM-DD'),
          location: this.location,
        },
        dataType: 'json',
        success: (events) => {
          this.loadedEventsOnce = true;
          this.rawEvents = events;
          this.cacheEvents(this.location, rangeString, events);
        },
      })
        .always(() => {
          this.loadingEvents = false;
          clearTimeout(this.eventsLoadingTimer);
        });
    },

    showEvent(event) {
      this.$refs.modal.show(event.title, event.url);
    },

    dateToInt(dateString) {
      return parseInt(dateString.replace(/-/g, ''), 10);
    },
  },

  created() {
    this.cache = {};
    this.eventsXhr = null;
    this.eventsLoadingTimer = null;
    this.refreshEvents();
  },

  template: `
    <div class="calendar">
      <div class="calendar__header">
        <h2 class="calendar__date" v-if="isMonthMode">{{ date.format('MMMM YYYY') }}</h2>
        <div class="calendar__nav" v-if="isMonthMode">
          <button type="button" class="small button" aria-label="Prev month" 
            @click="goPrevMonth" 
            :disabled="!allowGoPrevMonth"
          >
            ${svgIconHtml(leftIcon)}
          </button>
          <button type="button" class="small button" aria-label="Next month"
            @click="goNextMonth"
            :disabled="!allowGoNextMonth"
          >
            ${svgIconHtml(rightIcon)}
          </button>
        </div>
        <!--div class="calendar__filter">
          Show by location:
          <select v-model="location">
            <option value="*">All Locations</option>
            <option v-for="location in locations" :key="location" :value="location">{{ location }}</option>
          </select>
        </div-->
        <div class="calendar__actions">
          <div class="calendar__action">
            <button type="button" class="small secondary button"
              @click="mode = 'list'"
              :class="{ selected: isListMode }" aria-label="View as list">
              ${svgIconHtml(listIcon)}
              List
            </button>
            <button type="button" class="small secondary button"
              @click="mode = 'month'"
              :class="{ selected: isMonthMode }" aria-label="View as calendar">
              ${svgIconHtml(calendarIcon)}
              Month
            </button>
            
            <!--a :href="createUrl" class="small primary button">Add an Event</a-->
          </div>
        </div>
      </div>
      <div class="calendar__body" :class="{ 'grid-mode': isMonthMode, 'list-mode': isListMode }">
        <div class="loading" v-if="loadingEvents">
          <ui-progress-circular :size="72" :indeterminate="true"/>
        </div>
        
        <table v-if="isMonthMode" class="calendar__grid">
          <thead>
            <tr>
              <th scope="col">Sun</th>
              <th scope="col">Mon</th>
              <th scope="col">Tue</th>
              <th scope="col">Wed</th>
              <th scope="col">Thu</th>
              <th scope="col">Fri</th>
              <th scope="col">Sat</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(weekStart, weekIdx) in weeksOfPeriod" :key="weekIdx">
              <td v-for="(day, dayIdx) in daysOfWeek(weekStart)" :key="dayIdx"
                class="calendar__grid__day"
                :class="{ 'outside-of-month': !isSameMonth(day, date) }"
              >
                <div class="calendar__grid__day__num">{{ day.date() }}</div>
                <button
                  v-for="(event, eventIdx) in eventsOfDate(day)" :key="eventIdx"
                  class="calendar__grid__day__event"
                  @click="showEvent(event)"
                >
                  {{ event.title }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <div v-if="isListMode">  
          <div class="loading" v-if="loadingEvents">
            <ui-progress-circular :size="72" :indeterminate="true"/>
          </div>
          <div v-else class="calendar__list">
            <template v-if="!loadedEventsOnce">
              Loading...
            </template>
            <template v-else>
              <p v-if="!events.length">
                No events found.
              </p>
              <ListEvent
                  v-else
                  v-for="event in filteredEvents" 
                  :key="event.instanceId"
                  :start-date-text="event.startDate"
                  :end-date-text="event.endDate"
                  :title="event.title"
                  :url="event.url"
                  :photo="event.photo" />
            </template>
          </div>
        </div>
      </div>
      
      <EventInstanceModal ref="modal" />
    </div>
  `,
};

